import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

const iconOptions = ['Target', 'Zap', 'Users', 'Award', 'Shield', 'Star', 'TrendingUp', 'Heart'];

export default function AdminAboutPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    story_title: '',
    story_paragraphs: [],
    values_title: '',
    values_subtitle: '',
    why_choose_title: ''
  });
  const [valueCards, setValueCards] = useState([]);
  const [whyChoosePoints, setWhyChoosePoints] = useState([]);
  const [isValueModalOpen, setIsValueModalOpen] = useState(false);
  const [isWhyChooseModalOpen, setIsWhyChooseModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [editingWhyChoose, setEditingWhyChoose] = useState(null);
  const [valueForm, setValueForm] = useState({ icon: 'Target', title: '', description: '', order: 0 });
  const [whyChooseForm, setWhyChooseForm] = useState({ title: '', description: '', order: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, valuesRes, whyChooseRes] = await Promise.all([
        apiClient.get('/about-page-content'),
        apiClient.get('/value-cards'),
        apiClient.get('/why-choose-points')
      ]);
      setContent(contentRes.data);
      setValueCards(valuesRes.data);
      setWhyChoosePoints(whyChooseRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/about-page-content', content);
      toast.success('About page content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleAddParagraph = () => {
    setContent({ ...content, story_paragraphs: [...content.story_paragraphs, ''] });
  };

  const handleUpdateParagraph = (index, value) => {
    const newParagraphs = [...content.story_paragraphs];
    newParagraphs[index] = value;
    setContent({ ...content, story_paragraphs: newParagraphs });
  };

  const handleRemoveParagraph = (index) => {
    const newParagraphs = content.story_paragraphs.filter((_, i) => i !== index);
    setContent({ ...content, story_paragraphs: newParagraphs });
  };

  const handleSaveValueCard = async (e) => {
    e.preventDefault();
    try {
      if (editingValue) {
        await apiClient.put(`/admin/value-cards/${editingValue.id}`, valueForm);
        toast.success('Value card updated!');
      } else {
        await apiClient.post('/admin/value-cards', valueForm);
        toast.success('Value card created!');
      }
      setIsValueModalOpen(false);
      resetValueForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save value card');
    }
  };

  const handleDeleteValueCard = async (id) => {
    if (window.confirm('Delete this value card?')) {
      try {
        await apiClient.delete(`/admin/value-cards/${id}`);
        toast.success('Value card deleted!');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const handleSaveWhyChoose = async (e) => {
    e.preventDefault();
    try {
      if (editingWhyChoose) {
        await apiClient.put(`/admin/why-choose-points/${editingWhyChoose.id}`, whyChooseForm);
        toast.success('Point updated!');
      } else {
        await apiClient.post('/admin/why-choose-points', whyChooseForm);
        toast.success('Point created!');
      }
      setIsWhyChooseModalOpen(false);
      resetWhyChooseForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save point');
    }
  };

  const handleDeleteWhyChoose = async (id) => {
    if (window.confirm('Delete this point?')) {
      try {
        await apiClient.delete(`/admin/why-choose-points/${id}`);
        toast.success('Point deleted!');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetValueForm = () => {
    setValueForm({ icon: 'Target', title: '', description: '', order: 0 });
    setEditingValue(null);
  };

  const resetWhyChooseForm = () => {
    setWhyChooseForm({ title: '', description: '', order: 0 });
    setEditingWhyChoose(null);
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">About Page Management</h1>
          <p className="text-gray-600 mt-1">Manage all content on the /about page</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue="content" className="space-y-6">
          <TabsList>
            <TabsTrigger value="content">Main Content</TabsTrigger>
            <TabsTrigger value="values">Value Cards</TabsTrigger>
            <TabsTrigger value="why-choose">Why Choose Us</TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            <form onSubmit={handleSaveContent} className="bg-white rounded-lg shadow-md p-6 space-y-6">
              <div className="border-b pb-6">
                <h2 className="text-xl font-semibold mb-4">Hero Section</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Hero Title</Label>
                    <Input value={content.hero_title} onChange={(e) => setContent({ ...content, hero_title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Hero Subtitle</Label>
                    <Input value={content.hero_subtitle} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} />
                  </div>
                  <ImageUpload
                    label="Hero Background Image"
                    value={content.hero_image}
                    onChange={(url) => setContent({ ...content, hero_image: url })}
                    preview={true}
                  />
                </div>
              </div>

              <div className="border-b pb-6">
                <h2 className="text-xl font-semibold mb-4">Our Story</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Story Title</Label>
                    <Input value={content.story_title} onChange={(e) => setContent({ ...content, story_title: e.target.value })} />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Story Paragraphs</Label>
                      <Button type="button" size="sm" onClick={handleAddParagraph}>
                        <Plus size={16} className="mr-1" /> Add Paragraph
                      </Button>
                    </div>
                    {content.story_paragraphs.map((para, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Textarea
                          value={para}
                          onChange={(e) => handleUpdateParagraph(index, e.target.value)}
                          rows={3}
                          placeholder={`Paragraph ${index + 1}`}
                        />
                        <Button type="button" size="sm" variant="destructive" onClick={() => handleRemoveParagraph(index)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4">Section Titles</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Values Section Title</Label>
                    <Input value={content.values_title} onChange={(e) => setContent({ ...content, values_title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Values Section Subtitle</Label>
                    <Input value={content.values_subtitle} onChange={(e) => setContent({ ...content, values_subtitle: e.target.value })} />
                  </div>
                  <div>
                    <Label>Why Choose Us Title</Label>
                    <Input value={content.why_choose_title} onChange={(e) => setContent({ ...content, why_choose_title: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : 'Save Content'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="values">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Value Cards</h2>
                <Button onClick={() => setIsValueModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" /> Add Value Card
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {valueCards.map((card) => (
                  <div key={card.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{card.title}</h3>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingValue(card); setValueForm(card); setIsValueModalOpen(true); }}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteValueCard(card.id)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{card.description}</p>
                    <p className="text-xs text-gray-400 mt-2">Icon: {card.icon}, Order: {card.order}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="why-choose">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Why Choose Us Points</h2>
                <Button onClick={() => setIsWhyChooseModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" /> Add Point
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {whyChoosePoints.map((point) => (
                  <div key={point.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{point.title}</h3>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingWhyChoose(point); setWhyChooseForm(point); setIsWhyChooseModalOpen(true); }}>
                          <Edit size={16} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteWhyChoose(point.id)}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{point.description}</p>
                    <p className="text-xs text-gray-400 mt-2">Order: {point.order}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Value Card Modal */}
      <Dialog open={isValueModalOpen} onOpenChange={(open) => { if (!open) resetValueForm(); setIsValueModalOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingValue ? 'Edit Value Card' : 'Add Value Card'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveValueCard} className="space-y-4">
            <div>
              <Label>Icon</Label>
              <Select value={valueForm.icon} onValueChange={(value) => setValueForm({ ...valueForm, icon: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {iconOptions.map((icon) => (<SelectItem key={icon} value={icon}>{icon}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={valueForm.title} onChange={(e) => setValueForm({ ...valueForm, title: e.target.value })} required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={valueForm.description} onChange={(e) => setValueForm({ ...valueForm, description: e.target.value })} required rows={3} />
            </div>
            <div>
              <Label>Order</Label>
              <Input type="number" value={valueForm.order} onChange={(e) => setValueForm({ ...valueForm, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { resetValueForm(); setIsValueModalOpen(false); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={18} className="mr-2" />Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Why Choose Modal */}
      <Dialog open={isWhyChooseModalOpen} onOpenChange={(open) => { if (!open) resetWhyChooseForm(); setIsWhyChooseModalOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWhyChoose ? 'Edit Point' : 'Add Point'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveWhyChoose} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={whyChooseForm.title} onChange={(e) => setWhyChooseForm({ ...whyChooseForm, title: e.target.value })} required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={whyChooseForm.description} onChange={(e) => setWhyChooseForm({ ...whyChooseForm, description: e.target.value })} required rows={3} />
            </div>
            <div>
              <Label>Order</Label>
              <Input type="number" value={whyChooseForm.order} onChange={(e) => setWhyChooseForm({ ...whyChooseForm, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { resetWhyChooseForm(); setIsWhyChooseModalOpen(false); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={18} className="mr-2" />Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}